/**
 * Converts HEIC/HEIF files to JPEG so the browser can preview & resize them.
 * Non-HEIC files are passed through untouched.
 *
 * Strategy (in order):
 *   1. Try native `createImageBitmap` — Safari/iOS decode HEIC natively.
 *   2. Fall back to `heic-to` (libheif-js w/ HEVC support) for other browsers.
 *      Retry with different options and finally via a hidden <img> tag before
 *      we give up.
 */

async function fileToJpegBlob(blob: Blob, name: string): Promise<File> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();
  const out: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.9)
  );
  const newName = name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([out], newName, { type: 'image/jpeg', lastModified: Date.now() });
}

async function decodeViaImgTag(file: File): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('img decode failed'));
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d unavailable');
    ctx.drawImage(img, 0, 0);
    const blob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.9)
    );
    const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function convertHeicIfNeeded(file: File): Promise<File> {
  const isHeic =
    /image\/hei[cf]/i.test(file.type) ||
    /\.(heic|heif)$/i.test(file.name);
  if (!isHeic) return file;

  // Rewrap: iOS often serves HEIC files with empty File.type, which trips
  // strict MIME sniffers inside heic-to / heic2any.
  const typedBlob = file.type
    ? file
    : new File([file], file.name, { type: 'image/heic', lastModified: file.lastModified });

  // 1. Native path (Safari/iOS decode HEIC without help). Skip on Chromium
  // because createImageBitmap will "succeed" with a bogus 1x1 image on some
  // builds instead of throwing, producing corrupt JPEGs.
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  if (isSafari && typeof createImageBitmap === 'function') {
    try {
      return await fileToJpegBlob(typedBlob, file.name);
    } catch {
      // fall through
    }
  }

  // 2. heic-to (libheif-js with HEVC/hvc1 support).
  try {
    const { heicTo } = await import('heic-to');
    const blob = await heicTo({ blob: typedBlob, type: 'image/jpeg', quality: 0.9 });
    const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (err) {
    console.warn('[heic] heic-to failed, retrying with PNG target:', err);
  }

  // 3. heic-to retry with PNG target — some HEVC-encoded HEICs decode to PNG
  // but choke on the JPEG encoder path.
  try {
    const { heicTo } = await import('heic-to');
    const png = await heicTo({ blob: typedBlob, type: 'image/png', quality: 0.9 });
    return await fileToJpegBlob(png, file.name);
  } catch (err) {
    console.warn('[heic] heic-to PNG retry failed, trying <img> tag fallback:', err);
  }

  // 4. Chromium <img> fallback (rare, but harmless if it works).
  try {
    return await decodeViaImgTag(typedBlob as File);
  } catch (err) {
    console.warn('[heic] <img> fallback failed:', err);
  }

  // 5. Last-ditch: non-Safari createImageBitmap. Ordered last so we don't
  // accept a garbage bitmap when the real decoders could still work.
  if (!isSafari && typeof createImageBitmap === 'function') {
    try {
      const out = await fileToJpegBlob(typedBlob, file.name);
      // Guard against 1x1 stub bitmaps some Chromium builds return.
      if (out.size > 2048) return out;
    } catch (err) {
      console.warn('[heic] createImageBitmap fallback failed:', err);
    }
  }

  throw new Error(
    `Could not read ${file.name}. Please convert HEIC to JPG and try again.`
  );
}

export async function convertHeicBatch(
  files: File[],
  onProgress?: (done: number, total: number) => void
): Promise<File[]> {
  const out: File[] = [];
  const failed: string[] = [];
  let done = 0;
  onProgress?.(0, files.length);
  for (const f of files) {
    try {
      out.push(await convertHeicIfNeeded(f));
    } catch (err) {
      console.error('HEIC conversion failed for', f.name, err);
      failed.push(f.name);
    }
    done += 1;
    onProgress?.(done, files.length);
  }
  if (failed.length > 0) {
    const { toast } = await import('@/lib/toast');
    toast.warning(
      failed.length === 1
        ? `Skipped ${failed[0]} — couldn't read HEIC. Try converting to JPG.`
        : `Skipped ${failed.length} HEIC photos we couldn't read.`
    );
  }
  return out;
}
